import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowRight, CalendarClock, CloudSun, MapPinned, Ship, UsersRound, Waves } from "lucide-react";
import { api } from "@/lib/api";
import { useTerminal } from "@/context/TerminalContext";

const displayTime = value => value
  ? new Date(value).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
  : "Not scheduled";
const readinessClass = value => value === "Ready" ? "sev-ok" : value === "Blocked" ? "sev-critical" : "sev-warning";

export default function Dashboard() {
  const { currentTerminal, currentTerminalId, loading: terminalsLoading } = useTerminal();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [liveConditions, setLiveConditions] = useState(null);
  const [conditionsLoading, setConditionsLoading] = useState(false);
  const [conditionsError, setConditionsError] = useState("");
  const [conditionRefresh, setConditionRefresh] = useState(0);
  useEffect(() => {
    if (!currentTerminalId) return;
    let cancelled = false;
    setLiveConditions(null); setConditionsError(""); setConditionsLoading(true);
    api.conditions(currentTerminalId, conditionRefresh > 0)
      .then(result => { if (!cancelled) setLiveConditions(result); })
      .catch(err => { if (!cancelled) setConditionsError(err?.response?.data?.detail || "Conditions could not be loaded. Check the environmental source settings or retry."); })
      .finally(() => { if (!cancelled) setConditionsLoading(false); });
    return () => { cancelled = true; };
  }, [currentTerminalId, conditionRefresh]);

  useEffect(() => {
    if (!currentTerminalId) return;
    let cancelled = false;
    setData(null); setError("");
    api.dashboard(currentTerminalId)
      .then(result => { if (!cancelled) setData(result); })
      .catch(err => { if (!cancelled) setError(err?.response?.data?.detail || "Dashboard could not be loaded."); });
    return () => { cancelled = true; };
  }, [currentTerminalId]);

  const conditionRecord = liveConditions || data?.conditions;
  const conditions = useMemo(() => {
    const weather = conditionRecord?.weather || {};
    const marine = conditionRecord?.marine || {};
    return [["Wind", weather.wind_speed_knots, "kn"], ["Gust", weather.wind_gust_knots, "kn"], ["Sea level", marine.sea_level_m, "m"], ["Wave", marine.wave_height_m, "m"]];
  }, [conditionRecord]);

  if (terminalsLoading || (currentTerminalId && !data && !error)) return <div className="loading-state" role="status">Loading terminal overview…</div>;
  if (!currentTerminalId) return <div className="empty-state"><MapPinned size={24} /><strong>No accessible terminal</strong><span>Add or request access to a terminal to begin.</span></div>;
  if (error) return <div className="empty-state" role="alert"><AlertTriangle size={24} /><strong>Overview unavailable</strong><span>{error}</span></div>;

  const kpis = [
    { label: "Calls today", value: data.kpi.port_calls_today, icon: CalendarClock },
    { label: "Berths in use", value: data.kpi.berths_in_use, icon: MapPinned },
    { label: "Vessels alongside", value: data.kpi.vessels_alongside, icon: Ship },
    { label: "Needs attention", value: data.kpi.needs_attention, icon: AlertTriangle },
  ];

  return <div className="space-y-6">
    <header className="page-header">
      <div><p className="page-eyebrow">Overview</p><h1>Today at {currentTerminal?.name}</h1><p>Current movements, readiness checks, and work requiring a manager’s attention.</p></div>
      <Link to="/port-calls" className="button-link" data-testid="dashboard-go-port-calls">View port calls <ArrowRight size={15} /></Link>
    </header>

    <section className="metric-grid" data-testid="dashboard-kpi" aria-label="Terminal summary">
      {kpis.map(({ label, value, icon: Icon }) => <article className="metric-card" key={label}><Icon size={18} aria-hidden="true" /><span>{label}</span><strong>{value ?? 0}</strong></article>)}
    </section>

    <div className="dashboard-grid">
      <section className="surface-card dashboard-main">
        <div className="section-heading"><div><p className="page-eyebrow">Today’s calls</p><h2>Planned vessel movements</h2></div><span>{data.today.length} today</span></div>
        {data.today.length === 0 ? <div className="empty-state compact"><Ship size={20} /><span>No calls scheduled for today.</span></div> : <div className="manager-list">
          {data.today.map(call => <Link className="call-row" to={`/port-calls/${call.id}`} key={call.id} data-testid={`dashboard-pc-${call.id}`}>
            <div className="call-time"><span>ETA</span><strong>{displayTime(call.eta)}</strong></div>
            <div className="call-identity"><strong>{call.vessel_name || "Vessel not assigned"}</strong><span>{call.berth_name || "Berth not assigned"} · departs {displayTime(call.etd)}</span></div>
            <span className={`status-pill ${readinessClass(call.readiness)}`}>{call.readiness}</span><ArrowRight size={16} aria-hidden="true" />
          </Link>)}
        </div>}
      </section>

      <aside className="surface-card" data-testid="dashboard-conflicts-badge">
        <div className="section-heading"><div><p className="page-eyebrow">Needs attention</p><h2>Manager queue</h2></div><AlertTriangle size={18} /></div>
        {data.attention.length === 0 ? <div className="empty-state compact"><span>No deterministic blockers or warnings in upcoming calls.</span></div> : <div className="attention-list">
          {data.attention.slice(0, 8).map((item, index) => <Link to={`/port-calls/${item.port_call_id}`} key={`${item.port_call_id}-${item.code}-${index}`}><span className={`status-pill ${item.severity === "warning" ? "sev-warning" : "sev-critical"}`}>{item.severity}</span><strong>{item.vessel_name || "Port call"}</strong><p>{item.message}</p><small>{item.required_action || "Review the deterministic check and resolve before approval."}</small></Link>)}
        </div>}
      </aside>
    </div>

    <div className="dashboard-grid dashboard-grid-secondary">
      <section className="surface-card">
        <div className="section-heading"><div><p className="page-eyebrow">Next 24 hours</p><h2>Upcoming movements</h2></div><CalendarClock size={18} /></div>
        {data.next_24h.length === 0 ? <div className="empty-state compact"><span>No additional movements in the next 24 hours.</span></div> : <div className="manager-list condensed">{data.next_24h.map(call => <Link className="call-row" to={`/port-calls/${call.id}`} key={call.id}><div className="call-identity"><strong>{call.vessel_name || "Vessel not assigned"}</strong><span>{call.berth_name || "Berth not assigned"} · {displayTime(call.eta)}</span></div><span className={`status-pill ${readinessClass(call.readiness)}`}>{call.readiness}</span></Link>)}</div>}
      </section>
      <section className="surface-card">
        <div className="section-heading"><div><p className="page-eyebrow">Current conditions</p><h2>Weather and tides</h2></div><CloudSun size={18} /></div>
        <div className="condition-grid">{conditions.map(([label, value, unit]) => <div key={label}><span>{label}</span><strong className="engineering-value">{value ?? "—"}{value != null ? ` ${unit}` : ""}</strong></div>)}</div>
        <p className="freshness"><Waves size={14} /> {conditionRecord?.source_label || conditionRecord?.source || "No conditions loaded"}{conditionRecord?.ts ? ` · retrieved ${displayTime(conditionRecord.ts)}` : ""}</p>
        {conditionsLoading && <p role="status">Loading environmental conditions…</p>}
        {conditionsError && <p role="alert">{conditionsError}</p>}
        {conditionRecord?.provider_error && <p role="alert">{conditionRecord.provider_error_message || conditionRecord.provider_errors?.map(e=>`${e.provider || 'Provider'}: ${e.message}`).join('; ') || 'Provider data unavailable; check environmental source settings.'}</p>}
        {conditionRecord?.source === 'canada-chs-eccc' && <p className="text-xs">Reference data; station and datum verification still required. Wave data is not supplied by this integration.</p>}
        <div className="flex gap-3 mt-3"><button className="button-link" disabled={conditionsLoading} onClick={()=>setConditionRefresh(value=>value+1)}>Refresh conditions</button><Link className="button-link" to="/terminals">Environmental settings</Link></div>
      </section>
    </div>
    <p className="governance-callout"><UsersRound size={17} /> Readiness is derived from deterministic conflicts, workflow state, and required assignments. AI suggestions do not change readiness or approval status.</p>
  </div>;
}
